import { capitalCase as capitalCaseIdentifier, sentenceCase } from 'change-case';
import { flow, pipe } from 'fp-ts/lib/function.js';
import { init, last, map, replace, slice } from 'ramda';
import { rejectNilValues } from '../objects/objects.js';
/** Mirrors zod 3.23.8's email regex; spec locks equivalence on upgrade. */
export const regexEmail = /^(?!\.)(?!.*\.\.)([A-Z0-9_'+\-.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i;
export const joinWithOxfordComma = (array, separater = 'and') => {
    if (!array)
        return '';
    if (array.length === 1)
        return `${array[0]}`;
    // e.g. On Tuesday and Thursday
    if (array.length === 2)
        return `${array.join(` ${separater} `)}`;
    // e.g. On "Monday, Tuesday, and Wednesday"
    if (array.length > 2)
        return `${init(array).join(', ')}, ${separater} ${last(array)}`;
    return '';
};
export const collapseWhitespace = (text) => text.replace(/\s+/g, ' ').trim();
const capitalizeToken = (token) => token.replace(/^[^a-zA-Z]*[a-z]/, (match) => match.toUpperCase());
const isTitledWord = (text) => /^[A-Z][^_]*[a-z][^_]*$/.test(text) &&
    !/[a-z][A-Z]|[A-Z]{2}[a-z]/.test(text);
// Machine ids: change-case. Prose: keep punctuation.
export const capitalCase = (text) => /\s/.test(text) || isTitledWord(text)
    ? text.split(/(\s+)/).map(capitalizeToken).join('')
    : capitalCaseIdentifier(text);
export const appendText = (appendedText) => (prevText) => {
    return `${prevText}${appendedText}`;
};
export const prependText = (prependedText) => (prevText) => {
    return `${prependedText}${prevText}`;
};
/**
 * This function will allow the string you enter to be inferred as a constant
  @example
  const string = "test" // type of string
  const string = msg("test") // type of "test"
 */
export const msg = (s) => () => s;
export const getUpdatedFieldsText = flow(rejectNilValues, Object.keys, // Get keys of object
map(replace(/Id/g, '')), // remove the instances of 'Id' each key
map(sentenceCase), // convert properties to sentence case
joinWithOxfordComma, // join the keys with an Oxford comma
prependText('Updated '));
/**
 * Pluralize a word conditionally if `shouldPluralize` is true, a number !== 1, or an array with length !== 1
 *
 * Either takes two words, for singular and plural forms:
 * ```
 * (singularForm: string, pluralForm: string, shouldPluralize: boolean | number | any[] | undefined): string
 * ```
 *
 * Or takes a singular form and adds an 's' if pluralize is true
 * ```
 * (singularForm: string, shouldPluralize: boolean | number | any[] | undefined): string
 * ```
 *
 * Or takes an array of singular and plural form, plus a pluralize boolean
 * ```
 * ([singularForm, pluralForm]: [string, string], shouldPluralize: boolean | number | any[] | undefined): string
 * ```
 */
export const pluralize = (...params) => {
    const shouldPluralize = (value) => {
        if (Array.isArray(value))
            return value.length !== 1;
        else if (typeof value === 'number')
            return value !== 1;
        else
            return !!value;
    };
    if (params.length === 3 && typeof params[1] === 'string') {
        return shouldPluralize(params[2]) ? params[1] : params[0];
    }
    else if (Array.isArray(params[0])) {
        return shouldPluralize(params[1]) ? params[0][1] : params[0][0];
    }
    else {
        return `${params[0]}${shouldPluralize(params[1]) ? 's' : ''}`;
    }
};
export const trimTrailingSlash = (string) => string?.endsWith('/') ? slice(0, -1, string) : string;
export const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);
const nameWords = (name) => name.trim().split(/\s+/);
export const getFirstName = (name) => nameWords(name)[0];
/** The final word, or undefined for a one-word name */
export const getLastName = (name) => {
    const words = nameWords(name);
    return words.length > 1 ? last(words) : undefined;
};
// Show the first n items; remaining become '+ N more'.
export const truncateStringArray = (truncateAfter) => (array) => {
    const length = array.length;
    return pipe(array, (array) => array.slice(0, truncateAfter), (array) => length > truncateAfter
        ? [...array, `+ ${length - truncateAfter} more`]
        : array, (array) => array.join(', '));
};
//# sourceMappingURL=text.js.map